import { Button } from '@travelnest/merlin.button';
import * as React from 'react';
import { IImage } from '../../../../../api/images';
import { IProperty } from '../../../../../data/entities/property';
import { Images } from '../../../../../entities/images/constants';
import { Accordion, AccordionHeader, AccordionItem } from '../../../../components/accordion';
import { AirbnbPhotoImporter } from '../../../../components/airbnb-photo-import/AirbnbPhotoImporter';
import { ChecklistItem } from '../../../../components/checklist/ChecklistItem';
import { HelpLink } from '../../../../components/help-link/HelpLink';
import { Loader } from '../../../../components/loader/Loader';
import { Panel } from '../../../../components/panel/Panel';
import { Paragraph } from '../../../../components/paragraph/Paragraph';
import { PhotoLightbox } from '../../../../components/photo-lightbox/PhotoLightbox';
import { Progress } from '../../../../components/progress/Progress';
import { PhotoWall } from '../../../../components/tile-wall/PhotoWall';

const MAX_PHOTOS = 50;

export interface IPropertyEditPhotosProps {
	property: IProperty;
	loading: boolean;
	images: IImage[];
	uploading: boolean;
	processing: boolean;
	deleting: { images: IImage[], deleting: boolean };
	percent: number | null;
	errors: string;
	successes: IImage[];
	// Whether this component is running inside Task-Based Property,
	// and will use Merlin's Button
	buttonContext?: 'merlin' | 'portal';
	deletePhotos(photos: IImage[]): any;
	uploadPhotos(data: FileList): any;
	reorderPhotos(next: IImage[]): any;
}

interface IPropertyEditPhotosState {
	overlay: boolean;
	index: number;
	error?: string;
}

// NOTE: Task-Based Property is currently using this component to provide photos functionality.
// Please check it is OK to modify this there as well.
export class PropertyEditPhotos extends React.Component<IPropertyEditPhotosProps, IPropertyEditPhotosState> {
	private fileUploadRef: React.RefObject<any>;
	private showProgressBar: boolean = true;

	constructor(props: IPropertyEditPhotosProps) {
		super(props);
		this.state = { overlay: false, index: 0 };
		this.fileUploadRef = React.createRef();
		if (window.navigator.userAgent.indexOf('Edge') > -1) {
			// MS Edge has a known issue where it does not call the onprogress callback until progress is 100%
			this.showProgressBar = false;
		}
	}

	public processAndUploadFiles(files: FileList) {
		const total = this.props.images.length + files.length;
		if (total > MAX_PHOTOS) {
			this.setState({ ...this.state, error: `Please upload a maximum of ${MAX_PHOTOS} photos (${total} selected)` });
		} else {
			this.setState({ ...this.state, error: '' });
			this.props.uploadPhotos(files);
		}
	}

	public uploadPhotographsOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		this.fileUploadRef.current.click();
	}

	public editPhoto = (index: number) => {
		this.setState({ overlay: true, index });
	}

	public render() {

		const { overlay, index } = this.state;
		const images = this.props.images || [];

		const { property, loading, deleting, uploading, processing, percent, successes, errors, deletePhotos, reorderPhotos } = this.props;

		if (loading) return <Loader loading={true} />;

		const disabledIds = deleting && deleting.deleting ? deleting.images.map((i): string => i.id) : [];

		return (
			<div className="property-edit-photos">
				<PhotoLightbox
					isOpen={overlay}
					images={images}
					index={index}
					onRequestClose={() => this.setState({ overlay: false })}
				/>
				<Accordion>
					<AccordionHeader>Photos</AccordionHeader>
					<AccordionItem>
						{!(uploading && !processing) ? null :
							this.showProgressBar ? <Progress percent={Math.round(percent || 0)} /> : <Loader loading={true} message="Uploading images..." />
						}
						{!processing ? null :
							<Loader loading={true} message="Processing images..." />
						}
						<div className="property-edit-photos-buttons">
							{uploading || processing ? null :
								<Button onClick={this.uploadPhotographsOnClick}>Upload photos</Button>
							}
						</div>
						{errors && <div className="property-edit-photos__errors">
							<Panel type="error">
								<ul>
									<li>Error uploading file - please check that you chose a valid image file and try again</li>
								</ul>
							</Panel>
						</div>}

						{this.state.error && <div className="property-edit-photos__errors">
							<Panel type="error">
								<ul>
									<li>Error uploading photos - {this.state.error}</li>
								</ul>
							</Panel>
						</div>}

						{successes && successes.length && <div className="property-edit-photos__successes">
							<Panel>
								<p>Successfully uploaded {successes.length} {successes.length > 1 ? 'images' : 'image'}</p>
							</Panel>
						</div>}

						<input
							style={{ display: 'none' }}
							ref={this.fileUploadRef}
							type="file"
							name="images"
							multiple
							onChange={(e) => e.target.files && this.processAndUploadFiles(e.target.files)} />
						<Paragraph fillParent="mid">
							First impressions matter, so it's crucial that your photos are visually attractive and
							of a high quality. Follow our 3 tips for better photos:
						</Paragraph>
						<ChecklistItem
							type="success"
							text={`Add ${Images.MINIMUM_REQUIRED} photos as a minimum, ${Images.MINIMUM_RECOMMENDED}+ photos boosts listing performance`}
						/>
						<ChecklistItem type="success" text="Take and upload photos in landscape (horizontal) orientation, not portrait (vertical)"/>
						<ChecklistItem type="success" text="Include some external property photos" />
						<div className="property-edit-photos__guide-link">
							<HelpLink to="https://travelnest.freshdesk.com/support/solutions/articles/101000365055-how-to-add-re-order-and-delete-photos-and-import-from-an-existing-airbnb-listing">
								How to add, re-order and delete photos
							</HelpLink>
						</div>
						<div className="property-edit-photos__guide-link">
							<HelpLink to="https://travelnest.freshdesk.com/support/solutions/articles/101000365083-how-to-take-good-photos">
								How to take photos that appeal to guests
							</HelpLink>
						</div>
					</AccordionItem>
					<AccordionHeader collapsible startCollapsed>Import Airbnb photos</AccordionHeader>
					<AccordionItem>
						<AirbnbPhotoImporter property={property} buttonContext={this.props.buttonContext} />
					</AccordionItem>
					<AccordionHeader>Review photos</AccordionHeader>
					<AccordionItem>
						<PhotoWall
							images={images}
							disabled={disabledIds}
							onDelete={deletePhotos}
							onView={this.editPhoto}
							onReorder={reorderPhotos}
						/>
					</AccordionItem>
				</Accordion>
			</div >
		);
	}
}
